"use client";

import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import FeatureCard from "@/components/ui/feature-card";

/* Feature grid for the product pages. The tile itself is @/components/ui
   FeatureCard — the same surface, border, radius and text scale the homepage's
   supporting cards use — so a product feature grid and the homepage's Why
   Proxidize row read as one system. Only the grid and its choreography live here.

   Each card reveals as ONE object: the shell and its copy fade up together, so
   there is never a frame where a tile is on screen without its content. The card
   used to animate only its inner content (six empty boxes filling in
   afterwards); splitting it the other way round reads just as broken, so
   FeatureCard's own content reveal is switched off here via
   `revealContent={false}`.

   Each tile observes the viewport itself, so a row animates when that row is
   actually reached — on a one-column phone layout the grid is several screens
   tall, and a single container trigger would have fired all six off one card.
   The left-to-right sweep inside a row comes from a per-tile `custom` delay.

   Each tile also takes the accent wash and a pointer-tracked highlight, and its
   ambient visual runs its own loop while on screen (see ../feature-visuals).
   The visual sits directly on the card surface — no recessed well — so the
   animation reads as part of the tile rather than boxed off inside it. */

export interface FeatureGridItem {
  Visual: React.ComponentType;
  title: string;
  description: string;
}

/** Shared with the hero CTA and the dashboard tabs, so every entrance on the
 *  page decelerates on the same curve. */
const EASE = [0.22, 1, 0.36, 1] as const;

/** Per-column delay of the sweep. Small — a row at 0.07 is a wave, at 0.15 it is
 *  a queue. */
const STEP = 0.07;

/* The shared scrollRevealViewport pre-triggers 140px BEFORE an element reaches
   the fold, so content is ready a moment early. This grid sits ~140px under the
   hero, which put it right on that boundary: measured, a 39px layout settle
   during hydration pulled it inside the margin and the whole reveal ran at
   ~280ms while the section was still a full screen off-view. By the time you
   scrolled down the cards had already finished — which is exactly the "cards
   have no animation" report. Trigger on real entry instead. */
const tileViewport = { once: true, margin: "0px" } as const;

/* Deliberately plain: a fade and a short rise, nothing else. A scale-up made
   the tile read as a shrunken empty box for the first half of the move, which
   is the opposite of what a card full of content should do on arrival. */
const tileVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: EASE, delay },
  }),
};

const staticTileVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0 },
};

/** The grid runs 1 / 2 / `max` columns. Knowing which one is live lets the
 *  sweep run across a row instead of marching through the tiles in DOM order,
 *  which on a multi-up grid reads as a row-by-row queue.
 *
 *  Four-up waits for xl rather than lg: at 1024 it would divide the container
 *  into 225px cards with six-line descriptions, so that range stays two-up.
 *  Starts at `max` for SSR; the reveal is client-side on scroll, so first paint
 *  never depends on it. */
function useColumns(max: 2 | 3 | 4): number {
  const [cols, setCols] = useState<number>(max);

  useEffect(() => {
    const sm = window.matchMedia("(min-width: 640px)");
    const wide = window.matchMedia(max === 4 ? "(min-width: 1280px)" : "(min-width: 1024px)");
    const read = () => setCols(max > 2 && wide.matches ? max : sm.matches ? 2 : 1);
    read();
    sm.addEventListener("change", read);
    wide.addEventListener("change", read);
    return () => {
      sm.removeEventListener("change", read);
      wide.removeEventListener("change", read);
    };
  }, [max]);

  return cols;
}

interface Props {
  items: FeatureGridItem[];
  /** Labels the grid for assistive tech; the section heading supplies context. */
  ariaLabel?: string;
  /** Widest column count. Six features want three-up; four sit in a single
   *  four-up row, which keeps the card proportions close to the three-up grid
   *  instead of stretching two cards across the full container. */
  columns?: 2 | 3 | 4;
}

export default function FeatureGrid({ items, ariaLabel, columns = 3 }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const cols = useColumns(columns);

  /* Writes the cursor position onto the tile as percentages. Custom properties
     inherit, so the highlight can be painted by the card itself and get clipped
     by its own radius — see `.card-spotlight` in globals.css. */
  const trackPointer = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((event.clientX - rect.left) / rect.width) * 100}%`);
    el.style.setProperty("--my", `${((event.clientY - rect.top) / rect.height) * 100}%`);
  }, []);

  return (
    <div
      className={cn(
        "grid gap-4 sm:grid-cols-2 sm:gap-5",
        columns === 3 && "lg:grid-cols-3",
        columns === 4 && "xl:grid-cols-4"
      )}
      aria-label={ariaLabel}
      role="list"
    >
      {items.map((item, i) => (
        <motion.div
          key={item.title}
          role="listitem"
          variants={prefersReducedMotion ? staticTileVariants : tileVariants}
          initial="hidden"
          whileInView="visible"
          viewport={tileViewport}
          /* Column position only, never the index in the list. Tiles in a row
             enter together so the sweep runs left-to-right, the next row starts
             its own, and a one-column layout gets no delay at all. */
          custom={(i % cols) * STEP}
          onPointerMove={prefersReducedMotion ? undefined : trackPointer}
          className="feature-tile"
        >
          {/* card-wash keeps the grid off flat white on the light theme; its hue
              rotates every third sibling. The tiles are no longer the grid's
              direct children, so that rotation is re-anchored against these
              wrappers — see `.card-wash` in globals.css. card-hover is the
              shared lift; card-spotlight is the highlight that follows the
              --mx/--my written above. */}
          <FeatureCard
            Visual={item.Visual}
            title={item.title}
            description={item.description}
            wash
            revealContent={false}
            className="card-hover card-spotlight h-full"
          />
        </motion.div>
      ))}
    </div>
  );
}
